import PptxGenJS from "pptxgenjs"
import { flatJoin, splitTextInRect } from 'shared/util'
import { KojinResult } from 'interface/kojin'

import { slideFrom } from '../util/slideFrom'
import { KojinPptxComponentsProps, LayoutOption } from '../types'

import { continueText } from './continueText'

const sisetuListLayout = {
  header: {
    height: 0.6
  },
  column: {
    block: 0.8,
    prefecture: 1.0,
    //sisetuName,
    kojinNames: 4
  },
  margin: {
    cx: 0.1,
    cy: 0.05
  }
}
const colors = {
  border: 'ffffff',
  header: 'bcbdc0',
  even: 'e7e8e8',
  odd: 'f4f4f4'
}

type SisetuData = {
  sisetuCode: string
  sisetuName: string
  kojinList: {
    rank: number
    label: string
  }[]
}
type BlockData = {
  blockCode: string
  blockName: string
  prefectures: {
    prefectureCode: string
    prefectureName: string
    sisetuList: SisetuData[]
  }[]
}

export const buildBlockData = (kojinList: KojinResult[]) => {
  const blockList: BlockData[] = []
  let kojinIndex = 0
  for(const kojin of kojinList) {
    kojinIndex++
    if (!kojin.organ) continue

    for(const organ of kojin.organ) {
      if (!organ.sisetuName) continue

      let block = blockList.find((block) => block.blockCode === organ.sisetuBlockCode)
      if (!block) {
        block = {
          blockCode: organ.sisetuBlockCode ?? '',
          blockName: organ.sisetuBlockName ?? '不明',
          prefectures: []
        }
        blockList.push(block)
      }
      let prefecture = block.prefectures.find((prefecture) => prefecture.prefectureCode === organ.sisetuKenCode)
      if (!prefecture) {
        prefecture = {
          prefectureCode: organ.sisetuKenCode ?? '',
          prefectureName: organ.sisetuKenName ?? '不明',
          sisetuList: []
        }
        block.prefectures.push(prefecture)
      }
      let sisetu = prefecture.sisetuList.find((sisetu) => sisetu.sisetuCode === organ.sisetuCode)
      if (!sisetu) {
        sisetu = {
          sisetuCode: organ.sisetuCode,
          sisetuName: organ.sisetuName,
          kojinList: []
        }
        prefecture.sisetuList.push(sisetu)
      }
      const katagaki = flatJoin(' ', organ.bukaName, (organ.positionRank ?? 0) < 7500 ? organ.yakusyokuName : null)
      sisetu.kojinList.push({
        rank: organ.positionRank ?? 0,
        label: flatJoin(' - ',
          // eslint-disable-next-line no-irregular-whitespace
          `${(kojin.isiName ?? kojin.isiNameKana ?? 'No name').replace(/　/g, ' ')} 先生 (No.${kojinIndex+1})`,
          katagaki !== '' ? katagaki : null
        )
      })
    }
  }
  blockList.sort((a, b) => a.blockCode.localeCompare(b.blockCode))
  blockList.forEach((block) => {
    block.prefectures.sort((a, b) => a.prefectureCode.localeCompare(b.prefectureCode))
    block.prefectures.forEach((prefecture) => {
      prefecture.sisetuList.forEach((sisetu) => {
        sisetu.kojinList.sort((a, b) => a.rank - b.rank)
      })
    })
  })
  return blockList
}

const blockListHeader = (pptx: PptxGenJS, layout: LayoutOption, slide: PptxGenJS.Slide, title: string, pageNo: number) => {
  const top = 0
  const width = slideFrom(layout, 'width', 0, false)
  const height = sisetuListLayout.header.height

  slide.addText([{
    text: `${title}／施設一覧 - ${pageNo}`,
    options: {
      ...layout.textOption,
      fontSize: layout.baseFont.fontSize * 2.5,
      breakLine: true
    }
  }], {
    x: 0, y: top, w: width, h: height,
    margin: [20, 20, 5, 5], 
    align: 'left', valign: 'bottom',
    breakLine: true, wrap: false, autoFit: true
  })

  slide.addShape(pptx.ShapeType.line, {
    x: 0, y: height, w: width, h: 0,
    line: { color: "ad0026", width: 3 }
  })
}

type sisetuListProps = KojinPptxComponentsProps & {
  title: string
  blockList: BlockData[]
  scale?: number
}
type sisetuListReturnValue = {
	slide: PptxGenJS.Slide
  bottom: number
}
export const sisetuList = ({
  title,
  blockList,
  scale: nullableScale,
  ...other
}: sisetuListProps): sisetuListReturnValue => {
  const { pptx, layout, slide } = other
  const lineHeight = 0.017 * layout.baseFont.fontSize
  const scale = nullableScale ?? 1

  let pageNo = 0
  blockListHeader(pptx, layout, slide, title, ++pageNo)
  const tableTop = sisetuListLayout.header.height + layout.margin

  const header: PptxGenJS.TableRow[] = [[{
    text: '都道府県',
    options: {
      ...layout.cellOption,
      colspan: 2,
      fontSize: (layout.cellOption.fontSize ?? 0) * scale,
      fill: { color: colors.header },
      align: 'center'
    }
  }, {
    text: '施設名',
    options: {
      ...layout.cellOption,
      fontSize: (layout.cellOption.fontSize ?? 0) * scale,
      fill: { color: colors.header }
    }
  }, {
    text: '所属医師',
    options: {
      ...layout.cellOption,
      fontSize: (layout.cellOption.fontSize ?? 0) * scale,
      fill: { color: colors.header }
    }
  }]]
  const bottom = slideFrom(layout, 'bottom', 0)
  const sisetuNameWidth = slideFrom(layout, 'width', 
    (sisetuListLayout.column.block * scale) + 
    (sisetuListLayout.column.prefecture * scale) + 
    (sisetuListLayout.column.kojinNames * scale))

  let lastSlide = slide
  let at = tableTop + ((sisetuListLayout.margin.cy + (lineHeight * scale) + sisetuListLayout.margin.cy) * header.length)
  let body: PptxGenJS.TableRow[] = []

  const addRow = (rowNo: number, blockName: string, prefectureName: string, sisetu: SisetuData) => {
    const background = ((rowNo % 2) === 0) ? colors.even : colors.odd
    const sisetuNameLines = splitTextInRect(sisetu.sisetuName,
      layout.baseFont.fontSize * scale,
      layout.baseFont.fontFace,
      (sisetuNameWidth - (sisetuListLayout.margin.cx + sisetuListLayout.margin.cx)) * layout.inchPerDot
    ) || []
    const kojinNameLines = sisetu.kojinList.flatMap((kojin) => splitTextInRect(kojin.label,
      layout.baseFont.fontSize * scale,
      layout.baseFont.fontFace,
      (sisetuListLayout.column.kojinNames - (sisetuListLayout.margin.cx + sisetuListLayout.margin.cx)) * layout.inchPerDot
    ) || [])
    const rowHeight =
      sisetuListLayout.margin.cy + 
      (lineHeight * scale * Math.max(1, sisetuNameLines.length, kojinNameLines.length)) +
      sisetuListLayout.margin.cy

    if ((at + rowHeight) > bottom) {
      lastSlide.addTable(header.concat(body), {
        x: layout.margin, y: tableTop,
        colW: [
          sisetuListLayout.column.block * scale,
          sisetuListLayout.column.prefecture * scale,
          sisetuNameWidth,
          sisetuListLayout.column.kojinNames * scale
        ],
        border: { pt: 1, color: colors.border }
      })
      continueText({ ...other, slide: lastSlide, top: at })

      lastSlide = pptx.addSlide({ masterName: "MASTER_SLIDE" })
      body = []

      blockListHeader(pptx, layout, lastSlide, title, ++pageNo)
      at = tableTop + ((sisetuListLayout.margin.cy + (lineHeight * scale) + sisetuListLayout.margin.cy) * header.length)
    }
    body.push([{
      text: blockName,
      options: {
        ...layout.cellOption,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        fill: { color: background },
        align: 'center'
      }
    }, {
      text: prefectureName,
      options: {
        ...layout.cellOption,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        fill: { color: background },
        align: 'center'
      }
    }, {
      text: sisetuNameLines.join('\n'),
      options: {
        ...layout.cellOption,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        fill: { color: background }
      }
    }, {
      text: kojinNameLines.join('\n'),
      options: {
        ...layout.cellOption,
        fontSize: (layout.cellOption.fontSize ?? 0) * scale,
        fill: { color: background }
      }
    }])
    at += rowHeight
  }
  let rowNo = 0
  for(const block of blockList) {
    for(const prefecture of block.prefectures) {
      for(const sisetu of prefecture.sisetuList) {
        addRow(rowNo++, block.blockName, prefecture.prefectureName, sisetu)
      }
    }
  }
  if (body.length > 0) {
    lastSlide.addTable(header.concat(body), {
      x: layout.margin, y: tableTop,
      colW: [
        sisetuListLayout.column.block * scale,
        sisetuListLayout.column.prefecture * scale,
        sisetuNameWidth,
        sisetuListLayout.column.kojinNames * scale
      ],
      border: { pt: 1, color: colors.border }
    })
  }
  return {
    slide,
    bottom: at
  }
}
